import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import styled from 'styled-components';
import { useCommunicationsContext } from '../context/CommunicationsContext.tsx';
import { CommunicationActorsView } from '../detail/CommunicationActorsView.tsx';
import { CommunicationAttachments } from '../detail/CommunicationAttachments.tsx';
import { CommunicationForwardView } from '../detail/CommunicationForwardView.tsx';
import { CommunicationMessage } from '../detail/CommunicationMessage.tsx';
import { CommunicationSenderProtocol } from '../detail/CommunicationSenderProtocol.tsx';
import { CommunicationSubject } from '../detail/CommunicationSubject.tsx';
import { MarkCommunicationAsReadButton } from '../detail/MarkCommunicationAsReadButton.tsx';
import { getIsCommunicationCurrentUser } from '../getIsCommunicationCurrentUser.tsx';
import { CommunicationLogic } from '../logic/CommunicationLogic.ts';

const TableCellWrapper = styled.div`
  margin-left: 50px;
`;

const ExpandedRowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

interface ICommunicationTableExpandedRowProps {
  communication: ComunicazioneDTO;
}

/**
 * Riga estesa della Tabella delle comunicazioni
 */
export function CommunicationTableExpandedRow(
  props: ICommunicationTableExpandedRowProps,
) {
  const { communication } = props;
  const user = useCurrentUser();
  const { isBackoffice, procedureId } = useCommunicationsContext();

  const sender = CommunicationLogic.sender(communication);
  const isDraft = CommunicationLogic.isDraft(communication);
  const isCurrentUserSender = getIsCommunicationCurrentUser(
    sender?.id,
    user?.utenteLoggato,
  );
  const currentRecipient = CommunicationLogic.currentRecipient(communication, {
    isBackoffice,
    procedureId,
  });

  return (
    <TableCellWrapper>
      <ExpandedRowHeader>
        <CommunicationActorsView communication={communication} />
        <MarkCommunicationAsReadButton
          communication={communication}
          recipient={currentRecipient}
          isCurrentUserSender={isCurrentUserSender}
          isDraft={isDraft}
        />
      </ExpandedRowHeader>
      <CommunicationSenderProtocol
        communication={communication}
        withTopSpacing
      />
      <VerticalSpace size={6} />
      <CommunicationSubject subject={communication.oggetto} />
      <VerticalSpace size={2} />
      <CommunicationMessage message={communication.messaggio} />

      <VerticalSpace size={6} />
      {/*
       * Mostriamo le note addizionali solo in fase di draft, dato che poi vengono gestite
       * insieme al message in automatico dal BE. rif. FWP-DEV20.2 3.4.4
       */}
      {CommunicationLogic.isDraft(communication) && (
        <CommunicationMessage message={communication.noteAddizionali} />
      )}

      <div>
        <CommunicationAttachments attachments={communication.allegati} />
      </div>
      <CommunicationForwardView forward={communication.inoltro} />
    </TableCellWrapper>
  );
}
